import {
  Award, BookOpen, Briefcase, Check, CircleDashed, Clock, ExternalLink, FileText, GraduationCap, Hammer,
  Layers, ListChecks, Lock, Newspaper, Play, Terminal, Video,
} from 'lucide-react';

export const TRAIL_TYPES = {
  certification: { label: 'Certificação', short: 'passar no exame', goal: 'Passar num exame oficial', icon: Award, desc: 'Destino: aprovação no exame oficial.' },
  career: { label: 'Carreira', short: 'chegar a um cargo', goal: 'Chegar a um cargo', icon: Briefcase, desc: 'Destino: um cargo-alvo, com competências comprovadas em portfólio.' },
  project: { label: 'Projeto', short: 'entregar algo publicado', goal: 'Entregar algo publicado', icon: Hammer, desc: 'Destino: uma entrega publicada, como um repositório ou um deploy.' },
};

export const SEALS = {
  official: { label: 'Oficial', desc: 'Material do fornecedor do exame.', short: 'do fornecedor do exame' },
  complementary: { label: 'Complementar', desc: 'Aprofunda um tópico. Não é obrigatório.', short: 'aprofunda um tema' },
  report: { label: 'Relato', desc: 'Experiência de quem já fez a prova.', short: 'de quem já fez' },
  practice: { label: 'Prática', desc: 'Exercício hands-on, passo a passo.', short: 'mão na massa' },
};

// internal: recurso do próprio StudySpace (ícone lime em fundo de marca)
export const RESOURCE_TYPES = {
  course: { label: 'Curso', meta: 'Curso do StudySpace', icon: GraduationCap, internal: true },
  pdf: { label: 'PDF', meta: 'PDF', icon: FileText },
  link: { label: 'Link externo', meta: 'Link externo', icon: ExternalLink },
  article: { label: 'Artigo', meta: 'Artigo', icon: Newspaper, internal: true },
  questions: { label: 'Questões', meta: 'Questões', icon: ListChecks, internal: true },
  flashcards: { label: 'Flashcards', meta: 'Flashcards', icon: Layers, internal: true },
  practice: { label: 'Prática', meta: 'Prática hands-on', icon: Terminal },
  live: { label: 'Sessão ao vivo', meta: 'Sessão ao vivo', icon: Video },
};

export const PARTS = {
  study: { label: 'Estudar', icon: BookOpen },
  practice: { label: 'Praticar', icon: Terminal },
  checkpoint: { label: 'Checkpoint', icon: ListChecks },
};

/**
 * Estado de uma parte (Estudar · Praticar · Checkpoint) ou de um recurso.
 * Bloqueado é sequência, não permissão: libera ao concluir as partes anteriores.
 */
export const PART_STATUS = {
  pending: { label: 'Pendente', icon: CircleDashed, tone: 'pending' },
  doing: { label: 'Fazendo', icon: Play, tone: 'doing' },
  late: { label: 'Atrasado', icon: Clock, tone: 'late' },
  locked: { label: 'Bloqueado', icon: Lock, tone: 'locked' },
  done: { label: 'Concluído', icon: Check, tone: 'done' },
};

/** "Libera ao concluir Estudar e Praticar": as partes que vêm antes da bloqueada. */
export function unlockHint(parts, part) {
  const order = Object.keys(PARTS);
  const before = order.slice(0, order.indexOf(part)).filter(p => parts && p in parts).map(p => PARTS[p].label);
  if (before.length === 0) return 'Libera ao concluir as partes anteriores';
  const list = before.length === 1 ? before[0] : `${before.slice(0, -1).join(', ')} e ${before[before.length - 1]}`;
  return `Libera ao concluir ${list}`;
}

export const STATUS = {
  done: { label: 'Concluído', icon: Check, tone: 'done' },
  pending: { label: 'Pendente', icon: CircleDashed, tone: 'pending' },
  late: { label: 'Atrasado', icon: Clock, tone: 'late' },
  locked: { label: 'Bloqueado', icon: Lock, tone: 'locked' },
  doing: { label: 'Fazendo', icon: Play, tone: 'doing' },
};

export const WEEKDAYS = [
  { n: 1, short: 'Seg' }, { n: 2, short: 'Ter' }, { n: 3, short: 'Qua' }, { n: 4, short: 'Qui' },
  { n: 5, short: 'Sex' }, { n: 6, short: 'Sáb' }, { n: 7, short: 'Dom' },
];

/** 90 → "1h30"; 40 → "40 min"; 120 → "2h". */
export function fmtHours(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m === 0) return '0h';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`;
}

/** Só o número de horas, arredondado ("109h"). */
export function fmtHoursRound(minutes) {
  return `${Math.round((minutes || 0) / 60)}h`;
}

/** "1h30" → 90; "40 min" / "40" → 40; "2h" → 120. */
export function parseHours(text) {
  const t = String(text || '').trim().toLowerCase().replace(',', '.');
  if (!t) return 0;
  const hm = t.match(/^(\d+(?:\.\d+)?)\s*h\s*(\d+)?\s*(?:min)?$/);
  if (hm) return Math.round(parseFloat(hm[1]) * 60 + (hm[2] ? parseInt(hm[2], 10) : 0));
  const m = t.match(/^(\d+)\s*(?:min)?$/);
  if (m) return parseInt(m[1], 10);
  return NaN;
}

/** "Etapa 2" na URL: 0 = antes de começar, 99 = reta final. */
export function stageNumber(stage) {
  if (stage.kind === 'start') return 0;
  if (stage.kind === 'final') return 99;
  return stage.number;
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export function shortDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
}

/** Data local de hoje + n dias, em ISO (yyyy-mm-dd). */
export function isoDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Início dos breadcrumbs: Trilhas › Tipo › Trilha (o tipo abre o catálogo filtrado). */
export function trailCrumbs(navigate, { type, shortTitle, title, slug }) {
  return [
    { label: 'Trilhas', onClick: () => navigate('/trilhas') },
    { label: TRAIL_TYPES[type]?.label || 'Trilha', onClick: () => navigate(`/trilhas?tipo=${type}`) },
    { label: shortTitle || title, onClick: () => navigate(`/trilhas/${slug}`) },
  ];
}
